import { v5 } from "uuid";

import type {
  PinModalData,
  PinPreviewData,
} from "src/analytics/dashboard/PinChartModal";
import type { Dashboard } from "src/analytics/dashboard/useDashboard";
import type { LabelDictionaries } from "src/analytics/metrics/VisualizeMetrics/specialDomainScaling";
import { VisualizeMetricsToolMessage } from "src/analytics/metrics/VisualizeMetrics/VisualizeMetricsToolMessage";
import {
  computeToolCallStatus,
  ToolCallAccordionItem,
} from "src/base-components/agents/ToolCallDisplay";
import { ChatLoadingIndicator } from "src/base-components/Chat/ChatLoadingIndicator";
import { ChatLoadingSpinner } from "src/base-components/Chat/ChatLoadingSpinner";
import { ChatMessage } from "src/base-components/Chat/ChatMessage";
import { ChatTextContent } from "src/base-components/Chat/ChatTextContent";
import { ChatUserBubble } from "src/base-components/Chat/ChatUserBubble";
import {
  type SessionMessagesResponseMessagesInner,
  type ToolCallContentBlock,
  type ToolMessage,
  ToolResultStatus,
  ToolType,
} from "src/clients/agent-management";
import type { ChartDefinition } from "src/clients/analytics-dashboard";

const PIN_ID_NAMESPACE = "1b671a64-40d5-491e-99b0-da01ff1f3341";
const pinIdForToolCall = (toolCallId: string): string =>
  v5(toolCallId, PIN_ID_NAMESPACE);

const renderHumanContent = (content: unknown): string => {
  if (typeof content === "string") return content;
  return JSON.stringify(content);
};

// agent-management serves the raw tool name as `display_name`, so the chat
// shows "query_metrics" as the tool-call title. Until the server provides
// human-readable names (OPTI-7866), patch known tools here.
const TOOL_DISPLAY_NAMES: Record<string, string> = {
  query_metrics: "Query",
};

const withReadableToolName = (
  block: ToolCallContentBlock,
): ToolCallContentBlock => {
  const displayName = TOOL_DISPLAY_NAMES[block.name];
  return displayName
    ? { ...block, metadata: { ...block.metadata, display_name: displayName } }
    : block;
};

const buildToolResultMap = (
  messages: SessionMessagesResponseMessagesInner[],
): Map<string, ToolMessage> => {
  const entries = messages
    .filter((m): m is { type: "tool" } & ToolMessage => m.type === "tool")
    .map((m) => [m.tool_call_id, m] as const);
  return new Map(entries);
};

export type DashboardPinning = {
  dashboard: Dashboard;
  pinChart: (definition: ChartDefinition, id?: string, title?: string) => void;
  unpinChart: (id: string) => void;
  pinDisabled: boolean;
  pinDisabledReason?: string;
};

export const LiveAnalyticsMessages: React.FC<{
  messages: SessionMessagesResponseMessagesInner[];
  isActive: boolean;
  isProvisioning: boolean;
  failureMessage: string | null;
  labelDictionaries: LabelDictionaries;
  dataSource?: string;
  pinning?: DashboardPinning;
  onRequestPin?: (data: PinModalData) => void;
}> = ({
  messages,
  isActive,
  isProvisioning,
  failureMessage,
  labelDictionaries,
  dataSource,
  pinning,
  onRequestPin,
}) => {
  const toolResults = buildToolResultMap(messages);

  const sessionSubstatus = isActive && !isProvisioning ? "running" : null;

  const renderableMessages = messages.filter(
    (m) => m.type === "human" || m.type === "ai",
  );

  return (
    <>
      {renderableMessages.map((message, idx) => {
        const key = message.id ?? `${message.type}-${idx}`;
        if (message.type === "human") {
          return (
            <ChatMessage key={key} dataLoc="copilot-message-user" role="user">
              <ChatUserBubble message={renderHumanContent(message.content)} />
            </ChatMessage>
          );
        }
        // AI message
        return (
          <ChatMessage key={key} dataLoc="copilot-message-ai" role="assistant">
            <div className="flex flex-col gap-2">
              {(message.content_blocks ?? []).map((block, blockIdx) => {
                if (block.type === "text") {
                  return (
                    <ChatTextContent
                      key={`${key}-text-${blockIdx}`}
                      content={block.text}
                      delay={0}
                      useMarkdownRenderer
                    />
                  );
                }
                // Skip the structured-output tool (json_response) — it's the
                // agent's final answer, not a user-facing tool call.
                if (block.metadata.type === ToolType.STRUCTURED_OUTPUT) {
                  return null;
                }
                const result = toolResults.get(block.id) ?? null;
                if (
                  block.name === "reply_visualize_metrics" &&
                  result?.status !== ToolResultStatus.ERROR
                ) {
                  const pinId = pinIdForToolCall(block.id);
                  const isPinned =
                    pinning != null && pinId in pinning.dashboard.pins;

                  const handleRequestPin =
                    pinning && !isPinned && onRequestPin
                      ? (
                          sql: string,
                          preview: PinPreviewData,
                          parameterNames: string[],
                        ) =>
                          onRequestPin({
                            ...preview,
                            sql,
                            pinId,
                            parameterNames,
                          })
                      : undefined;

                  return (
                    <VisualizeMetricsToolMessage
                      key={`${key}-tool-${block.id}`}
                      dataSource={dataSource}
                      labelDictionaries={labelDictionaries}
                      onRequestPin={handleRequestPin}
                      onUnpin={
                        pinning && isPinned
                          ? () => pinning.unpinChart(pinId)
                          : undefined
                      }
                      pinDisabled={pinning?.pinDisabled}
                      pinDisabledReason={pinning?.pinDisabledReason}
                      toolCall={block}
                      toolResult={result}
                    />
                  );
                }
                return (
                  <ToolCallAccordionItem
                    key={`${key}-tool-${block.id}`}
                    showDescription={false}
                    showTokenUsage={false}
                    status={computeToolCallStatus(result, sessionSubstatus)}
                    toolCall={withReadableToolName(block)}
                    toolResult={result}
                  />
                );
              })}
            </div>
          </ChatMessage>
        );
      })}
      {isActive &&
        (isProvisioning ? (
          <ChatLoadingSpinner
            dataLoc="copilot-provisioning"
            text="Setting up agent..."
          />
        ) : (
          <ChatLoadingIndicator mode="loading" dataLoc="copilot-thinking" />
        ))}
      {failureMessage && (
        <li
          className="font-inter-normal-12px text-red-600"
          data-loc="copilot-error"
        >
          {failureMessage}
        </li>
      )}
    </>
  );
};
